import { AvatarComponent } from '@/app/common/components/avatar/avatar.component';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { NotificationCenterService } from '@/app/common/services/notification-center/notification-center.service';
import { Notice, NoticeStatus } from '@/app/modules/notification/core/types';
import { AsyncPipe, DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  map,
  switchMap,
  tap,
} from 'rxjs';

enum NotificationTab {
  Unread = 0,
  Viewed = 1,
}

const TAB_STATUS: Record<NotificationTab, NoticeStatus[]> = {
  [NotificationTab.Unread]: [NoticeStatus.SENT],
  [NotificationTab.Viewed]: [NoticeStatus.VIEW, NoticeStatus.READ],
};

@Component({
  selector: 'az-blc-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  standalone: true,
  imports: [
    NzTabsModule,
    NzLayoutModule,
    NzGridModule,
    NzButtonModule,
    NzEmptyModule,
    NzSpinModule,
    NgIf,
    NgForOf,
    NgClass,
    DatePipe,
    AsyncPipe,
    MatIconModule,
    AvatarComponent,
  ],
})
export class AppNotificationComponent implements OnInit, OnDestroy {
  @Output() closeDrawer = new EventEmitter<void>();

  readonly unreadTab = NotificationTab.Unread;

  notices: Notice[] = [];

  status = NoticeStatus;

  isLoading = new BehaviorSubject<boolean>(false);

  selectedTabIndex = NotificationTab.Unread;

  private readonly pageSize = 20;
  private readonly loadTab$ = new Subject<NotificationTab>();
  private subscription = new Subscription();

  constructor(
    private router: Router,
    private loaderService: LoaderService,
    private notificationCenter: NotificationCenterService
  ) {
    this.subscription.add(
      this.loadTab$
        .pipe(
          tap((tab) => {
            this.selectedTabIndex = tab;
            this.notices = [];
          }),
          switchMap((tab) =>
            this.loaderService
              .executeWithLoader(
                this.notificationCenter.loadNotices({
                  pageIndex: 1,
                  pageSize: this.pageSize,
                  status: TAB_STATUS[tab],
                }),
                this.isLoading
              )
              .pipe(map((response) => ({ tab, items: response.items })))
          )
        )
        .subscribe(({ tab, items }) => {
          this.notices = items.filter((notice) =>
            TAB_STATUS[tab].includes(notice.status)
          );
        })
    );
  }

  ngOnInit(): void {
    this.loadTab$.next(NotificationTab.Unread);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.isLoading.complete();
    this.loadTab$.complete();
  }

  onDrawerOpen() {
    this.selectedTabIndex = NotificationTab.Unread;
    this.loadTab$.next(NotificationTab.Unread);
  }

  onTabChange(index: number) {
    if (index === this.selectedTabIndex) {
      return;
    }

    this.loadTab$.next(index as NotificationTab);
  }

  onNoticeClick(notice: Notice) {
    if (notice.status === NoticeStatus.SENT && notice.id) {
      this.subscription.add(
        this.notificationCenter.markAsViewed(notice.id).subscribe({
          next: () => {
            this.notices = this.notices.filter((item) => item.id !== notice.id);
            this.followLink(notice);
          },
        })
      );
      return;
    }

    this.followLink(notice);
  }

  markAllAsRead() {
    if (this.selectedTabIndex !== NotificationTab.Unread) {
      return;
    }

    this.subscription.add(
      this.loaderService
        .executeWithLoader(
          this.notificationCenter.markAllAsRead(),
          this.isLoading
        )
        .subscribe({
          next: () => this.loadTab$.next(NotificationTab.Unread),
        })
    );
  }

  private followLink(notice: Notice) {
    if (!notice.link) {
      return;
    }

    this.closeDrawer.emit();
    this.router.navigateByUrl(notice.link);
  }
}
